import React from 'react';
import type { Issue } from '../../types/issue';
import { isTransitionAllowed } from '../../types/issue';
import type { EnterpriseAction } from '../../services/actionService';
import type { IssueMeasureRecord, StandardizationEventRecord } from '../../services/issueMaspService';
import { efficacyAttempts, evaluateIssueClosure, latestEfficacyAttempt } from '../../utils/efficacyCheck';
import { isCauseConfirmed } from '../../utils/problemCause';
import { formatMeasureValue, presentMeasureComparison } from '../../utils/issueMasp';
import { standardizationReadiness } from '../../utils/standardization';
import { conclusionAllowsClose, conclusionDurationDays } from '../../utils/conclusion';

interface ConclusionWorkspaceProps {
  issue: Issue;
  actions: EnterpriseAction[];
  measures: IssueMeasureRecord[];
  events: StandardizationEventRecord[];
  pending?: boolean;
  loadError?: boolean;
  actionsState?: 'loading' | 'error';
}

export const ConclusionWorkspace: React.FC<ConclusionWorkspaceProps> = ({
  issue,
  actions,
  measures,
  events,
  pending = false,
  loadError = false,
  actionsState,
}) => {
  const direct = actions.filter((action) => action.problem_id === issue.id);
  const completed = direct.filter((action) => action.status === 'completed');
  const phenomenon = issue.root_cause_analysis?.problem_statement?.trim()
    || issue.description?.trim()
    || issue.title;
  const confirmed = isCauseConfirmed(issue);
  const cause = issue.root_cause_analysis?.root_cause_description?.trim()
    || issue.root_cause?.trim()
    || '';
  const attempts = efficacyAttempts(issue.metadata);
  const closure = evaluateIssueClosure({
    actions: direct,
    attempts,
    closureReason: issue.metadata?.closure_reason,
    metadata: issue.metadata,
  });
  const efficacyValid = closure.case === 'B' && closure.canClose;
  const latest = latestEfficacyAttempt(attempts);
  const readiness = standardizationReadiness(events);
  const comparisons = measures.map(presentMeasureComparison);
  const closed = issue.status === 'closed';
  const allowed = conclusionAllowsClose({
    status: issue.status,
    closure,
    standardizationReady: readiness.ready,
    transitionAllowed: isTransitionAllowed(issue.mode, issue.status, 'closed'),
  });
  const days = conclusionDurationDays(issue.created_at, issue.resolved_at);
  const reason = issue.metadata?.closure_reason?.trim() || '';
  const closedOn = issue.resolved_at ? new Date(issue.resolved_at).toLocaleDateString('pt-BR') : '';

  return (
    <div className="max-w-[960px] space-y-3">
      {closed ? (
        <p className="text-[13px] font-medium text-slate-950">
          Problema encerrado{closedOn ? ` · ${closedOn}` : ''}{reason ? ` · ${reason}` : ''}
        </p>
      ) : (
        <p className="text-[13px] text-slate-600">
          {allowed ? 'Encerramento liberado.' : 'Encerramento ainda não liberado.'}
        </p>
      )}

      <section>
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Problema</p>
        <p className="mt-0.5 text-[14px] text-slate-950">{phenomenon}</p>
      </section>
      <section>
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Causa</p>
        <p className="mt-0.5 text-[13px] text-slate-800">
          {confirmed && cause ? cause : 'A causa ainda não está consolidada.'}
        </p>
      </section>
      <section>
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Contramedidas</p>
        {actionsState === 'loading' && direct.length === 0 ? (
          <p className="mt-0.5 text-[13px] text-slate-600">Carregando contramedidas.</p>
        ) : actionsState === 'error' && direct.length === 0 ? (
          <p className="mt-0.5 text-[13px] text-slate-600">Não foi possível carregar as contramedidas.</p>
        ) : completed.length === 0 ? (
          <p className="mt-0.5 text-[13px] text-slate-600">Nenhuma contramedida concluída.</p>
        ) : (
          <ul className="mt-0.5">
            {completed.map((action) => (
              <li key={action.id} className="text-[13px] text-slate-800">{action.title}</li>
            ))}
          </ul>
        )}
      </section>
      <section>
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Resultado</p>
        {pending && comparisons.length === 0 ? (
          <p className="mt-0.5 text-[13px] text-slate-600">Carregando medidas.</p>
        ) : loadError && comparisons.length === 0 ? (
          <p className="mt-0.5 text-[13px] text-slate-600">Não foi possível carregar as medidas.</p>
        ) : comparisons.length === 0 ? (
          <p className="mt-0.5 text-[13px] text-slate-600">Nenhuma medida registrada.</p>
        ) : (
          <ul className="mt-0.5">
            {comparisons.map((item) => (
              <li key={item.id} className="text-[13px] text-slate-800">
                {item.name} · {formatMeasureValue(item.before)} {item.unit} → {formatMeasureValue(item.after)} {item.unit} · {item.result}
              </li>
            ))}
          </ul>
        )}
        <p className="mt-1 text-[13px] text-slate-800">
          {efficacyValid ? 'Eficaz para as contramedidas concluídas.' : 'Ainda sem eficácia que cubra as contramedidas.'}
          {latest ? ` · ${new Date(latest.at).toLocaleDateString('pt-BR')}` : ''}
        </p>
      </section>
      <section>
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Padronização</p>
        <p className="mt-0.5 text-[13px] text-slate-800">{readiness.recorded} de {readiness.total} itens registrados</p>
      </section>
      {days != null ? (
        <p className="text-[12px] text-slate-500">{days === 1 ? '1 dia' : `${days} dias`}</p>
      ) : null}
    </div>
  );
};
